import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mail, ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import About from '../components/About';
import Services from '../components/Services';
import Process from '../components/Process';
import CaseStudies from '../components/CaseStudies';
import Testimonials from '../components/Testimonials';
import TechStack from '../components/TechStack';
import FinalCTA from '../components/FinalCTA';
import WhatsAppFloat from '../components/WhatsAppFloat';

const InstagramIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="3.5" />
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

const LinkedInIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const Home = () => {
  return (
    <div className="min-h-screen bg-[#030712] text-white flex flex-col font-sans selection:bg-sky-500 selection:text-white">
      {/* Fixed Navigation Header */}
      <Navbar />

      {/* Main Landing Sections */}
      <main className="flex-1">
        <Hero />
        <About />
        <Services />
        <Process />
        <CaseStudies />
        <Testimonials />
        <TechStack />
        <FinalCTA />
      </main>

      {/* WhatsApp Floating Contact Icon */}
      <WhatsAppFloat />

      {/* Editorial Footer */}
      <footer className="border-t border-white/10 bg-slate-950 text-slate-400 font-sans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">

            {/* Brand Column (4 cols) */}
            <div className="md:col-span-4 flex flex-col justify-between space-y-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl overflow-hidden border border-white/15 flex-shrink-0">
                    <img src="/logo.png" alt="Zynqora Edge" className="w-full h-full object-cover" />
                  </div>
                  <span className="font-heading font-extrabold text-lg text-white tracking-tight">
                    Zynqora Edge
                  </span>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed font-normal max-w-sm">
                  Enterprise software systems, AI agents, and workflow automation built for modern companies that demand performance and scale.
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                {[
                  { href: 'https://www.instagram.com/zynqora.edge', title: 'Instagram', Icon: InstagramIcon },
                  { href: 'https://www.linkedin.com/in/haris-kamran', title: 'LinkedIn', Icon: LinkedInIcon },
                  { href: 'https://wa.me/12395177689', title: 'WhatsApp', Icon: MessageCircle },
                  { href: 'mailto:haris@harisbuilds.com', title: 'Email', Icon: Mail },
                ].map((item) => (
                  <a
                    key={item.title}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={item.title}
                    className="w-9 h-9 rounded-lg bg-slate-900 border border-white/10 flex items-center justify-center text-slate-300 hover:text-sky-400 hover:border-sky-400/40 transition-colors"
                  >
                    <item.Icon size={15} />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links Column (3 cols) */}
            <div className="md:col-span-3">
              <h4 className="font-heading text-xs font-bold uppercase tracking-widest text-slate-300 mb-4">
                Quick Navigation
              </h4>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <a href="#about" className="hover:text-white transition-colors">About Us</a>
                </li>
                <li>
                  <a href="#services" className="hover:text-white transition-colors">Services Overview</a>
                </li>
                <li>
                  <a href="#process" className="hover:text-white transition-colors">How We Work</a>
                </li>
                <li>
                  <a href="#cases" className="hover:text-white transition-colors">Case Studies &amp; Proof</a>
                </li>
                <li>
                  <a href="#reviews" className="hover:text-white transition-colors">USA &amp; Canada Reviews</a>
                </li>
                <li>
                  <a href="#tech" className="hover:text-white transition-colors">Infrastructure Stack</a>
                </li>
              </ul>
            </div>

            {/* Disciplines Column (3 cols) */}
            <div className="md:col-span-3">
              <h4 className="font-heading text-xs font-bold uppercase tracking-widest text-slate-300 mb-4">
                Core Disciplines
              </h4>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link to="/ai-chatbot" className="hover:text-sky-400 transition-colors flex items-center justify-between">
                    <span>AI Chatbots &amp; Agents</span>
                    <ArrowUpRight size={12} />
                  </Link>
                </li>
                <li>
                  <Link to="/automation" className="hover:text-sky-400 transition-colors flex items-center justify-between">
                    <span>Workflow Automation</span>
                    <ArrowUpRight size={12} />
                  </Link>
                </li>
                <li>
                  <Link to="/saas-development" className="hover:text-sky-400 transition-colors flex items-center justify-between">
                    <span>SaaS Development</span>
                    <ArrowUpRight size={12} />
                  </Link>
                </li>
                <li>
                  <span className="text-slate-400">Website &amp; Mobile Apps</span>
                </li>
                <li>
                  <span className="text-slate-400">SEO &amp; GEO Optimization</span>
                </li>
              </ul>
            </div>

            {/* Contact Info (2 cols) */}
            <div className="md:col-span-2">
              <h4 className="font-heading text-xs font-bold uppercase tracking-widest text-slate-300 mb-4">
                Contact HQ
              </h4>
              <div className="space-y-3 text-sm">
                <a href="mailto:haris@harisbuilds.com" className="hover:text-white transition-colors block text-xs truncate">
                  haris@harisbuilds.com
                </a>
                <a href="https://wa.me/12395177689" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors block text-xs">
                  +1 239 517 7689
                </a>
                <span className="text-xs text-slate-400 block">
                  Islamabad HQ &amp; North America
                </span>
              </div>
            </div>

          </div>

          {/* Bottom Legal Copyright Bar */}
          <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
            <p>&copy; {new Date().getFullYear()} Zynqora Edge. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" className="hover:text-slate-300 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-and-conditions" className="hover:text-slate-300 transition-colors">
                Terms &amp; Conditions
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;